import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    Checkbox,
    Autocomplete,
    TextField,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import userService from "../../services/userService";
import deliveryReceiptService from "../../services/deliveryReceiptService";

const CreateDeliveryReceiptDialog = ({ open, onClose, onCreated }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerInput, setCustomerInput] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [shipments, setShipments] = useState([]);
    const [shipmentsLoading, setShipmentsLoading] = useState(false);
    // Map<itemId, shipmentId> — every currently-selected item, however it
    // was selected (individually or via a shipment's "select all").
    const [selectedItems, setSelectedItems] = useState(new Map());

    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) {
            setSelectedCustomer(null);
            setCustomerInput("");
            setCustomerOptions([]);
            setShipments([]);
            setSelectedItems(new Map());
        }
    }, [open]);

    // Debounced customer search
    useEffect(() => {
        if (!open) return;
        const query = customerInput.trim();
        if (query.length < 2) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        const timer = setTimeout(() => {
            userService
                .getAll({ role: "customer", search: query, limit: 20 })
                .then(({ data }) => setCustomerOptions(data.data || []))
                .catch(() => setCustomerOptions([]))
                .finally(() => setCustomerLoading(false));
        }, 1000);
        return () => clearTimeout(timer);
    }, [customerInput, open]);

    // Load this customer's items that are still free to go on a NEW receipt
    // once one is selected — grouped by shipment, so the admin can pick a
    // whole shipment at once or hand-pick specific items from it.
    useEffect(() => {
        if (!selectedCustomer) {
            setShipments([]);
            return;
        }
        setShipmentsLoading(true);
        setSelectedItems(new Map());
        deliveryReceiptService
            .getEligibleItems(selectedCustomer._id)
            .then(({ data }) => setShipments(data.data.shipments || []))
            .catch(() => setShipments([]))
            .finally(() => setShipmentsLoading(false));
    }, [selectedCustomer]);

    const toggleItem = (shipmentId, itemId) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            next.has(itemId) ? next.delete(itemId) : next.set(itemId, shipmentId);
            return next;
        });
    };

    const toggleShipment = (shipment) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            const allSelected = shipment.items.every((it) => next.has(it._id));
            shipment.items.forEach((it) => {
                if (allSelected) {
                    next.delete(it._id);
                } else {
                    next.set(it._id, shipment._id);
                }
            });
            return next;
        });
    };

    const handleCreate = async () => {
        if (selectedItems.size === 0) return;
        setCreating(true);
        try {
            const items = Array.from(selectedItems, ([itemId, shipmentId]) => ({ shipmentId, itemId }));
            const { data } = await deliveryReceiptService.create(items);
            enqueueSnackbar(`Delivery receipt ${data.data.receipt.receiptNumber} created`, {
                variant: "success",
            });
            onCreated?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to create delivery receipt", {
                variant: "error",
            });
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>New Delivery Receipt</Typography>
                <IconButton size="small" onClick={onClose}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                    Customer
                </Typography>
                <Autocomplete
                    options={customerOptions}
                    loading={customerLoading}
                    value={selectedCustomer}
                    onChange={(_, val) => setSelectedCustomer(val)}
                    inputValue={customerInput}
                    onInputChange={(_, val) => setCustomerInput(val)}
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => (o ? `${o.firstName} ${o.lastName} (${o.email})` : "")}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    noOptionsText={
                        customerInput.trim().length < 2 ? "Type at least 2 characters…" : "No customers found"
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Search customer *" placeholder="Search by name or email…" />
                    )}
                    sx={{ mb: 3 }}
                />

                {selectedCustomer && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                            Select a whole shipment, or specific items from it
                        </Typography>

                        {shipmentsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : shipments.length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                This customer has no items available for a new delivery receipt — everything is
                                either already delivered or already on another open receipt.
                            </Alert>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {shipments.map((s) => {
                                    const allSelected = s.items.every((it) => selectedItems.has(it._id));
                                    const someSelected = !allSelected && s.items.some((it) => selectedItems.has(it._id));
                                    return (
                                        <Box key={s._id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                            <Box
                                                onClick={() => toggleShipment(s)}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    p: 1,
                                                    px: 1.5,
                                                    bgcolor: "action.hover",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Checkbox
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                />
                                                <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>
                                                    {s.trackingNumber}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({s.items.length} item{s.items.length !== 1 ? "s" : ""})
                                                </Typography>
                                            </Box>
                                            {s.items.map((it) => (
                                                <Box
                                                    key={it._id}
                                                    onClick={() => toggleItem(s._id, it._id)}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.5,
                                                        p: 1,
                                                        pl: 3,
                                                        cursor: "pointer",
                                                        borderTop: "1px solid",
                                                        borderColor: "divider",
                                                        bgcolor: selectedItems.has(it._id) ? "rgba(13,98,155,0.04)" : "transparent",
                                                    }}
                                                >
                                                    <Checkbox checked={selectedItems.has(it._id)} size="small" sx={{ p: 0.5 }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" noWrap>
                                                            {it.description || "Item"}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {it.trackingNumber || "—"} · {it.itemStatusLabel}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={selectedItems.size === 0 || creating}
                >
                    {creating ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        `Create Receipt${selectedItems.size > 0 ? ` (${selectedItems.size} item${selectedItems.size !== 1 ? "s" : ""})` : ""}`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateDeliveryReceiptDialog;
